import { Breakpoint, Typography } from '@altibox/lte-salgskanaler-design-system-component-lib';
import React, { FunctionComponent, ReactNode } from 'react';
import { Button, ButtonProps } from '@lyse-as/formds-react';

import { CustomerDebtAlert } from 'src/components/customer-debt-alert';
import { MaximumReachedAlert } from 'src/components/maximum-reached-alert';
import { usePurchaseEligibilityQueryStateOrThrow } from 'src/hooks/use-purchase-eligibility-query';
import {
  strommeboxLeftPictureProps,
  strommeboxTopPictureProps,
  strommeboxBackPictureProps,
} from 'src/resources/picture';
import { goToStepActionCreator } from 'src/state/order/action-creators/go-to-step-action-creator';
import { OrderStep } from 'src/state/order/types';
import { useAppDispatch } from 'src/state/thunk-api';
import { i18n } from 'src/i18n';
import { currencyWithSuffix } from 'src/utils/currency-utils';

import {
  StyledAlertContainer,
  StyledContainer,
  StyledLink,
  StyledImagesSlider,
  StyledPrice,
  StyledPriceContainer,
  StyledPrices,
  StyledReturnNote,
  StyledTitle,
} from './product-details.styled';

interface Price {
  text: string;
  price: number;
}

interface Props {
  title: string;
  content: ReactNode;
  mainPrice?: Price;
  secondaryPrice?: Partial<Price>;
  link?: {
    productDetailsPage: OrderStep;
    text: string;
  };
  buttonProps: ButtonProps;
  showReturnNote?: boolean;
  showQuantityAlert?: boolean;
}

export const ProductDetails: FunctionComponent<Props> = (props) => {
  const { content, mainPrice, secondaryPrice, buttonProps, showReturnNote, title, link, showQuantityAlert } = props;

  const dispatch = useAppDispatch();
  const purchaseEligibility = usePurchaseEligibilityQueryStateOrThrow();
  const isInDebt = purchaseEligibility.status === 'DUNNING';

  const renderPrices = () => (
    <StyledPrices>
      {mainPrice && (
        <StyledPriceContainer>
          <Typography variant="headline5" component="p">
            {mainPrice.text}
          </Typography>
          <StyledPrice variant="headline5" component="p">
            {currencyWithSuffix(mainPrice.price)}
          </StyledPrice>
        </StyledPriceContainer>
      )}
      {secondaryPrice && (
        <StyledPriceContainer>
          <Typography variant="paragraph2" component="p">
            {secondaryPrice.text}
          </Typography>
          {secondaryPrice.price && (
            <StyledPrice variant="paragraph2" component="p">
              {currencyWithSuffix(secondaryPrice.price)}
            </StyledPrice>
          )}
        </StyledPriceContainer>
      )}
    </StyledPrices>
  );

  return (
    <StyledContainer>
      <StyledImagesSlider
        images={[strommeboxLeftPictureProps, strommeboxTopPictureProps, strommeboxBackPictureProps]}
      />
      <StyledTitle variant="headline2" component="h1">
        {title}
      </StyledTitle>
      {content}
      {!showQuantityAlert && (mainPrice || secondaryPrice) && renderPrices()}
      <div>
        <Button {...buttonProps} disabled={isInDebt || buttonProps.disabled} />
      </div>
      {showReturnNote && (
        <StyledReturnNote variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
          {i18n.component.productDetails.returnNote}
        </StyledReturnNote>
      )}
      {link && (
        <StyledLink
          onClick={() => {
            dispatch(goToStepActionCreator(link.productDetailsPage));
          }}
        >
          {link.text}
        </StyledLink>
      )}
      {(isInDebt || showQuantityAlert) && (
        <StyledAlertContainer>
          {showQuantityAlert && <MaximumReachedAlert />}
          {isInDebt && <CustomerDebtAlert />}
        </StyledAlertContainer>
      )}
    </StyledContainer>
  );
};
